import React from "react";
import { useTranslation } from "react-i18next";
import { UiVersion } from "../../uiVersion";
import { LEGACY_ONLY } from "../../killSwitch";
import { useVersionSwitch } from "./useVersionSwitch";
import { useTutorial } from "../tutorial/TutorialContext"; // GUNDI-5665, temporary

// GUNDI-5639 / GUNDI-5642 / GUNDI-5665: the switch between the new portal and
// the frozen legacy one. New code (not a frozen import), mounted by both trees'
// Navs.
//
// Not dismissible (product call): the switch is the whole point of the trial,
// so it stays on screen wherever it is shown.
type VersionBannerProps = { variant: UiVersion };

// GUNDI-5665: both trees advertise the switch now — the new tree because it is
// the default and the way back must never be more than one click away, legacy
// because the way forward has to be discoverable. So there is nothing left to
// match on: the strip shows on every page of both trees.
//
// Kept as the single source of truth shared with the padding wrappers, so the
// strip and the 28px compensation can never disagree.
export function useVersionBannerVisible(variant: UiVersion): boolean {
    // GUNDI-5665: both trees advertise the switch now — except while the
    // LEGACY_ONLY kill switch is on, when there is nothing to switch to.
    return !LEGACY_ONLY;
}

// GUNDI-5665 (TEMPORARY): the tour entry point. Its own component because
// hooks cannot be conditional and useTutorial() only has a provider in the new
// tree — the legacy variant never renders this. `hidden lg:inline` (1024px):
// the tour is built for laptops and larger — it never considers phone/tablet
// layouts — so smaller screens do not get the link at all.
function TutorialLink() {
    const { t } = useTranslation();
    const { requestStart } = useTutorial();
    return (
        <>
            <span aria-hidden="true" className="hidden lg:inline text-xs text-ui-text-primary-text">·</span>
            <button
                type="button"
                data-testid="VersionBanner-tutorial-button"
                onClick={requestStart}
                // Purple on purpose (product call): the tour entry point should
                // stand out from the banner's gray switch link.
                className="hidden lg:inline text-xs font-semibold underline underline-offset-2 text-unlisted-purple hover:text-unlisted-dark-purple"
            >
                {t("Take a tour")}
            </button>
        </>
    );
}

export default function VersionBanner({ variant }: VersionBannerProps) {
    const { t } = useTranslation();
    const { switchTo, switching } = useVersionSwitch();
    const isNew = variant === "new";
    const visible = useVersionBannerVisible(variant);
    if (!visible) return null;

    // Slim full-width strip mounted inside the fixed nav wrapper, directly
    // under the environment stripe. Kept ≤28px tall: the app shell's
    // pt-[143px] leaves room for the banner below the 86px nav without
    // covering page content.
    return (
        // New-variant layout (GUNDI-5665, product call): the message + tour link
        // sit CENTERED, the switch link is pinned to the RIGHT on md+; below md
        // the message hides and only the switch link remains. The legacy variant
        // keeps the simple centered pair.
        <div
            data-testid="VersionBanner-div"
            // Both strips are white. The legacy one was light purple for a
            // while, to read as visibly not-the-default; it looked bad against
            // the rest of the page, so the wording carries that on its own now.
            className="relative flex flex-row items-center justify-center gap-2 px-4 h-7 bg-white border-b border-unlisted-gray-lines"
        >
            <span className={`${isNew ? "hidden md:inline" : ""} text-xs font-normal text-ui-text-primary-text`}>
                {isNew
                    ? t("You're viewing the new version of Gundi.")
                    : t("You're viewing the previous version of Gundi.")}
            </span>
            {isNew && <TutorialLink />}
            <button
                type="button"
                data-testid="VersionBanner-button"
                disabled={switching}
                onClick={() => switchTo(isNew ? "legacy" : "new")}
                className={`${isNew ? "md:absolute md:right-4 md:top-1/2 md:-translate-y-1/2" : ""} text-xs font-semibold underline underline-offset-2 disabled:opacity-50 text-ui-text-secondary-text hover:text-ui-text-primary-text`}
            >
                {isNew ? t("Switch back to the previous version") : t("Switch to the new version")}
            </button>
        </div>
    );
}
